import React, { useEffect, useState, useCallback } from 'react';
import api from '../services/api';
import {
  Box, Typography, Paper, Button, Alert, AlertTitle, CircularProgress,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Chip, Stack, Divider, Tooltip, IconButton,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Storage as StorageIcon,
  Warning as WarningIcon,
  Help as HelpIcon,
} from '@mui/icons-material';

// Compact display of a rack-units value: "1U" / "2U" / "0.5U" / "?"
const fmtRU = (ru) => (ru == null ? '?' : `${ru}U`);

const fmtNum = (n) => (n == null ? '—' : new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 }).format(Number(n)));

export default function RackBuilder({ projectId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoDetecting, setAutoDetecting] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    try {
      const r = await api.get(`/projects/${projectId}/racks`);
      setData(r.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAutoDetect = async () => {
    setAutoDetecting(true);
    setError(null);
    try {
      await api.post(`/projects/${projectId}/racks/auto-detect`);
      await fetchData();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setAutoDetecting(false);
    }
  };

  if (loading && !data) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>;
  }

  if (!data) return null;

  const { racks, review_queue, unmatched_locations, proposal_id } = data;
  const noProposal = !proposal_id;
  const noRacks = (racks || []).length === 0;

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <StorageIcon sx={{ color: '#0D2C54' }} />
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#0D2C54' }}>Rack Builder</Typography>
          <Typography variant="body2" color="text.secondary">
            Pulled from the D-Tools proposal. AV-1 / Network rooms become racks; rack-shaped items in other rooms are flagged for review.
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <span>
            <IconButton onClick={fetchData} disabled={loading}><RefreshIcon /></IconButton>
          </span>
        </Tooltip>
        <Button
          variant="contained"
          onClick={handleAutoDetect}
          disabled={autoDetecting || noProposal}
          startIcon={autoDetecting ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{ bgcolor: '#0D2C54', '&:hover': { bgcolor: '#16294A' } }}
        >
          {noRacks ? 'Auto-Detect Racks from Proposal' : 'Re-Detect'}
        </Button>
      </Stack>

      {noProposal && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>No D-Tools proposal yet</AlertTitle>
          This project doesn't have a D-Tools proposal synced. Sync the proposal first, then return here.
        </Alert>
      )}

      {!noProposal && noRacks && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No racks created yet. Click <b>Auto-Detect Racks from Proposal</b> to scan the proposal for AV-1 / Network rooms.
        </Alert>
      )}

      {/* Unmatched-rack-rooms hint */}
      {unmatched_locations && unmatched_locations.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <AlertTitle>New rack rooms detected</AlertTitle>
          <Typography variant="body2" sx={{ mb: 1 }}>
            These rack-named rooms exist in the proposal but haven't been added as racks yet. Click <b>Re-Detect</b> to add them.
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {unmatched_locations.map(u => (
              <Chip key={u.location_name} size="small" label={`${u.location_name} (${u.item_count})`} />
            ))}
          </Stack>
        </Alert>
      )}

      {/* Rack cards */}
      {racks.map(rack => (
        <Paper key={rack.id} variant="outlined" sx={{ mb: 3, overflow: 'hidden' }}>
          <Box sx={{ p: 2, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>{rack.name}</Typography>
              {rack.location_name && rack.location_name !== rack.name && (
                <Chip size="small" variant="outlined" label={`D-Tools: ${rack.location_name}`} />
              )}
              <Box sx={{ flexGrow: 1 }} />
              <Chip size="small" label={`${rack.total_items} item${rack.total_items === 1 ? '' : 's'}`} />
              <Chip
                size="small"
                color="primary"
                label={`${fmtNum(rack.total_ru_known)} / ${rack.rack_height_u}U used`}
              />
              {rack.items_unknown_ru > 0 && (
                <Tooltip title="Items missing RU data in product master — manual entry or scrape needed">
                  <Chip
                    size="small"
                    color="warning"
                    icon={<HelpIcon />}
                    label={`${rack.items_unknown_ru} unknown RU`}
                  />
                </Tooltip>
              )}
            </Stack>
          </Box>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 'bold', width: 60 }}>Qty</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: 80 }}>RU</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Manufacturer</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Model</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Category</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="right">Weight (lb)</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="right">Power (W)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rack.items.length === 0 && (
                  <TableRow><TableCell colSpan={7} sx={{ color: 'text.secondary', textAlign: 'center', py: 4 }}>
                    No items in this rack location.
                  </TableCell></TableRow>
                )}
                {rack.items.map(it => {
                  const ru = it.slot?.rack_units_override ?? it.rack_units;
                  const unknown = ru == null && it.rack_mountable !== false;
                  return (
                    <TableRow key={it.dtools_item_id} hover>
                      <TableCell>{it.quantity}</TableCell>
                      <TableCell>
                        {unknown
                          ? <Chip size="small" color="warning" variant="outlined" label="?" sx={{ height: 20 }} />
                          : <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{fmtRU(ru)}</Typography>
                        }
                      </TableCell>
                      <TableCell>{it.manufacturer || '—'}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{it.model || '—'}</TableCell>
                      <TableCell><Typography variant="caption" color="text.secondary">{it.category}</Typography></TableCell>
                      <TableCell align="right">{fmtNum(it.weight_lbs)}</TableCell>
                      <TableCell align="right">{fmtNum(it.power_watts)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      ))}

      {/* Review queue */}
      {review_queue && review_queue.length > 0 && (
        <Paper variant="outlined" sx={{ mb: 3, overflow: 'hidden' }}>
          <Box sx={{ p: 2, bgcolor: 'warning.lighter', borderBottom: '1px solid', borderColor: 'divider' }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <WarningIcon color="warning" />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Review Queue</Typography>
              <Chip size="small" color="warning" label={review_queue.length} />
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Rack-shaped items (amplifiers, processors, sources, switchers) that landed in non-rack rooms.
              These may belong in a rack or stay in the room — confirm with engineering.
            </Typography>
          </Box>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 'bold', width: 60 }}>Qty</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Manufacturer</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Model</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Category</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Currently In</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {review_queue.map(it => (
                  <TableRow key={it.dtools_item_id} hover>
                    <TableCell>{it.quantity}</TableCell>
                    <TableCell>{it.manufacturer || '—'}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{it.model || '—'}</TableCell>
                    <TableCell><Typography variant="caption" color="text.secondary">{it.category}</Typography></TableCell>
                    <TableCell><Typography variant="body2">{it.location_name || '—'}</Typography></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <Divider sx={{ my: 2 }} />
      <Typography variant="caption" color="text.secondary">
        Phase 1 — read-only view. Drag-drop layout, RU auto-scrape, and PDF export are coming next.
      </Typography>
    </Box>
  );
}
